/**
 * OTLP (OpenTelemetry Protocol) 형식 변환 유틸리티
 *
 * LogEntry를 OTLP ExportTraceServiceRequest 형식으로 변환합니다.
 * 서버의 /v1/traces 엔드포인트와 호환됩니다.
 */

import type { Attributes, LogEntry } from '../types';

// ============================================
// OTLP 타입 정의
// ============================================

interface OtlpKeyValue {
  key: string;
  value: {
    stringValue?: string;
    intValue?: number;
    doubleValue?: number;
    boolValue?: boolean;
  };
}

interface OtlpSpan {
  traceId: string;
  spanId: string;
  name: string;
  kind: number;
  startTimeUnixNano: string;
  endTimeUnixNano: string;
  attributes: OtlpKeyValue[];
  status: { code: number };
}

interface OtlpExportTraceServiceRequest {
  resourceSpans: Array<{
    resource: {
      attributes: OtlpKeyValue[];
    };
    scopeSpans: Array<{
      scope: {
        name: string;
        version: string;
      };
      spans: OtlpSpan[];
    }>;
  }>;
}

// ============================================
// ID 생성 유틸리티
// ============================================

/**
 * 16자리 hex 문자열 생성 (spanId용)
 */
function generateSpanId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * 32자리 hex 문자열 생성 (traceId용)
 */
function generateTraceId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// ============================================
// 변환 함수
// ============================================

/**
 * Attributes를 OTLP KeyValue 배열로 변환
 */
function toOtlpAttributes(
  attrs?: Attributes | Record<string, string>,
): OtlpKeyValue[] {
  if (!attrs) return [];

  return Object.entries(attrs).map(([key, value]) => {
    if (typeof value === 'string') {
      return { key, value: { stringValue: value } };
    } else if (typeof value === 'number') {
      // OTLP: 정수만 intValue. 소수를 intValue 로 넣으면 collector 가
      // "assertInteger: can not decode float as int" 로 **배치 전체를 400** 처리해,
      // 같은 flush 배치에 묶인 다른 이벤트(enter_map 등)까지 동반 유실된다.
      // 소수는 doubleValue, 비유한(NaN/Infinity)은 스칼라 부적합이라 문자열로 보존.
      if (Number.isInteger(value)) {
        return { key, value: { intValue: value } };
      }
      if (Number.isFinite(value)) {
        return { key, value: { doubleValue: value } };
      }
      return { key, value: { stringValue: String(value) } };
    } else if (typeof value === 'boolean') {
      return { key, value: { boolValue: value } };
    } else {
      // 배열은 문자열로 변환
      return { key, value: { stringValue: JSON.stringify(value) } };
    }
  });
}

/**
 * LogEntry 배열을 OTLP ExportTraceServiceRequest 형식으로 변환
 *
 * @param entries - 변환할 LogEntry 배열
 * @param service - 서비스 이름 (resource.service.name)
 * @returns OTLP 형식의 trace request 객체
 *
 * @example
 * ```typescript
 * const entries = [{ id: '1', timestamp: Date.now(), type: 'log', event: 'click' }];
 * const otlpPayload = toOtlpTraceRequest(entries, 'my-app');
 * fetch('/v1/traces', { body: JSON.stringify(otlpPayload) });
 * ```
 */
export function toOtlpTraceRequest(
  entries: LogEntry[],
  service: string,
): OtlpExportTraceServiceRequest {
  const spans: OtlpSpan[] = entries.map(entry => {
    const traceId = generateTraceId();
    const spanId = generateSpanId();
    const startTimeNano = entry.timestamp * 1_000_000; // ms → ns
    const endTimeNano = startTimeNano + 1_000_000; // 1ms duration

    // OTLP attributes는 배열이라 같은 key가 중복으로 들어갈 수 있다.
    // event attributes가 globalAttributes를 override하도록 객체 병합 후 변환한다.
    const mergedAttributes = {
      ...entry.globalAttributes,
      ...entry.attributes,
    };
    const allAttributes: OtlpKeyValue[] = [
      { key: 'event.name', value: { stringValue: entry.event } },
      { key: 'event.type', value: { stringValue: entry.type } },
      ...toOtlpAttributes(mergedAttributes),
    ];

    return {
      traceId,
      spanId,
      name: entry.event,
      kind: 1, // SPAN_KIND_INTERNAL
      startTimeUnixNano: String(startTimeNano),
      endTimeUnixNano: String(endTimeNano),
      attributes: allAttributes,
      status: { code: 1 }, // STATUS_CODE_OK
    };
  });

  return {
    resourceSpans: [
      {
        resource: {
          attributes: [
            { key: 'service.name', value: { stringValue: service } },
          ],
        },
        scopeSpans: [
          {
            scope: {
              name: '@zep/logger',
              version: '1.0.0',
            },
            spans,
          },
        ],
      },
    ],
  };
}
